import { convertTimeByGranularity } from "@@/shared/date-time/time-granularity";
import {
    timeRangeByDurationFactory,
    timeRangeFactory,
} from "@@/shared/date-time/time-range";
import { repeatIndex } from "@@/utilities/repeat";
import { sortBy } from "@@/utilities/sort";
import { uniqueBy } from "@@/utilities/unique";
import type { BookableSession } from "./bookable-session";

const calculateMaxSessionLengths = (session: BookableSession) => {
    const max = session.durationMinMax.max ?? 1;

    return timeRangeByDurationFactory(session.start, {
        value: max,
        granularity: session.durationGranularity,
    });
};

const calculatePossibleSessionLengths = (session: BookableSession) => {
    const min = session.durationMinMax.min;
    const max = session.durationMinMax.max ?? 1;

    if (min >= max || session.durationBaseLength < 1)
        return [
            timeRangeByDurationFactory(session.start, {
                value: min,
                granularity: session.durationGranularity,
            }),
        ];

    const numberOfOver =
        (max - session.durationMinMax.min) / session.durationBaseLength;

    return repeatIndex(numberOfOver + 1).map(i => {
        return timeRangeByDurationFactory(session.start, {
            value: session.durationBaseLength * i + min,
            granularity: session.durationGranularity,
        });
    });
};

const getLongestPossibleSessionLength = (session: BookableSession) => {
    const sessionLengths = calculatePossibleSessionLengths(session);
    return sessionLengths[sessionLengths.length - 1];
};

//* Calculate all possible session lengths
//* that are within the range of the session
//* spanning means it will need to match the start of another session.
const calculateSpanningTimeRanges = (
    session: BookableSession,
    sessions: BookableSession[],
) => {
    const longest = getLongestPossibleSessionLength(session);

    const allMatchingSessionInRange = sessions.filter(
        s =>
            s.start.unix >= session.start.unix &&
            s.start.unix <= longest.end.unix &&
            s._id !== session._id, // Exclude the session itself
    );

    const asMinSeconds = convertTimeByGranularity(
        session.durationMinMax.min,
        session.durationGranularity,
        "SECOND",
    );
    const asMaxSeconds = convertTimeByGranularity(
        session.durationMinMax.max ?? 0,
        session.durationGranularity,
        "SECOND",
    );

    //Calculate all possible session lengths
    const minTimes = allMatchingSessionInRange.flatMap(
        s => calculatePossibleSessionLengths(s)[0],
    );

    const all = minTimes
        .flatMap(t => {
            //Removed max for spaning because of bug with cykel&langd
            //const max = timeRangeFactory(session.start, t.end);
            const min = timeRangeFactory(session.start, t.start);

            //If the min duration is less than the min duration of the session, return only the max
            //These could be if there are overlapping sessions that are shorter than the min duration
            if (min.duration < asMinSeconds) return [];

            return [min];
        }) //Filter out all sessions that are longer than the max duration
        .filter(t => t.duration <= asMaxSeconds);
    return uniqueBy(all, t => t._id).sort(sortBy(t => t.duration));
};

export {
    calculateMaxSessionLengths,
    calculatePossibleSessionLengths,
    calculateSpanningTimeRanges,
};
